/**
 * Which org should v2 select when it boots?
 *
 * Inside the v3 shell v2 does not own its session: every request is signed with the
 * access token the parent handed over, and api-svc rejects any `orgId` variable other
 * than the one that token was issued for ("Context creation failed: User not in org",
 * surfaced as a 500). The only safe choice is therefore the token's own org. The
 * `orgId` cookie / first-org fallbacks exist for the standalone app, where picking a
 * different org triggers a session refresh for it instead of a stream of 500s — and
 * the cookie is host-scoped to app.bitwave.io, so it just remembers the last org ANY
 * tab opened in the iframe, which is exactly how the mismatch happens (CIB-4184).
 */
export function resolveInitialOrgId(opts: {
  /** The user's org ids, as returned by the `orgs` query. */
  orgIds: string[];
  /** `orgId` claim of the access token the app will sign requests with. */
  tokenOrgId?: string;
  /** Last org remembered in the `orgId` cookie. */
  cookieOrgId?: string;
  /** Orgs that need an explicit escalation step and must never be auto-selected. */
  escalationOrgIds?: string[];
}): string | undefined {
  const { orgIds, tokenOrgId, cookieOrgId, escalationOrgIds = [] } = opts;

  if (tokenOrgId && orgIds.includes(tokenOrgId)) {
    return tokenOrgId;
  }
  if (cookieOrgId && orgIds.includes(cookieOrgId) && !escalationOrgIds.includes(cookieOrgId)) {
    return cookieOrgId;
  }
  return orgIds[0];
}

/**
 * `orgId` claim of a JWT access token, or undefined when the token is missing or not
 * parseable. Deliberately signature-blind: the token is only used to pick the org the
 * server will already enforce, never to grant anything.
 */
export function orgIdFromAccessToken(token: string | undefined | null): string | undefined {
  if (!token) return undefined;
  const parts = token.split('.');
  if (parts.length < 2) return undefined;
  try {
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4);
    // Same byte → percent-encoding → UTF-8 decode as AuthService.parseJwt.
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    const payload = JSON.parse(json);
    return typeof payload?.orgId === 'string' && payload.orgId ? payload.orgId : undefined;
  } catch {
    return undefined;
  }
}
